import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import {
  Tip, NextUp, KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation34() {
  const { lesson, course, accent } = findLesson("piano-foundation", "34");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Take a song you know — and use it to drill the scales, rhythm patterns, and chord-inversion routines from earlier lessons.</Highlight>
        <Highlight>Practising drills inside a song forces them to feel <strong>musical</strong> — a clean scale that doesn't groove with the song isn't yet useful.</Highlight>
        <Highlight>Pick the scale that matches the song's key. Apply Lessons 25-28 fingering. Use the song's chord changes to switch hand positions in real time.</Highlight>
        <Highlight>Right hand handles the scale-walking and improv; left hand carries the song's chord progression with the patterns from Lessons 12, 14, 19.</Highlight>
        <Highlight>The drill becomes <strong>practice as music</strong>, not practice as exercise.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The point of this lesson" title="Take what you know · plug it into a real song" accent={accent.hex}>
        <p>
          Up to now, scales and finger drills have lived inside isolated routines. This
          short lesson asks for the next step — pick a song, apply every technique from the
          course inside it, and let the song be the practice frame.
        </p>

        <p>
          This strengthens your knowledge of rhythm and improves control over your fingers —
          while having a lot of fun, of course.
        </p>
      </Insight>

      <Insight eyebrow="The recipe" title="Match the scale · use the chords · walk the routines" accent={accent.hex}>
        <ol className="space-y-2 text-[14px]">
          <li>
            <strong>Pick a song you know.</strong> Folk, pop, gospel, anything that fits one
            scale all the way through.
          </li>
          <li>
            <strong>Find the scale.</strong> Determine the key — Lesson 8's "scale degree"
            method is the cleanest way: hum the song, find the home note, identify the scale.
          </li>
          <li>
            <strong>Apply scale fingering.</strong> Lessons 25-28 cover the fingering rules
            for every white and black scale, both hands. Use them.
          </li>
          <li>
            <strong>Lock in the chord progression.</strong> Use the chords from Lesson 13
            (Tonic, Pre-Dominant, Dominant) — most folk and pop songs use only those three.
          </li>
          <li>
            <strong>Pick a left-hand pattern.</strong> Lessons 12, 14, 19 give you Pulse,
            Toggle, Oom-Pah, Tresillo, Arpeggio, Waltz. Match the pattern to the song's mood.
          </li>
          <li>
            <strong>Improvise on top.</strong> Use the scale to walk a melody between the
            song's structural points (verse, chorus, bridge). Sing along with what you play.
          </li>
        </ol>

        <Tip>
          The song you pick matters less than the routine you build around it. Even
          "Twinkle, Twinkle" played seven different ways — seven different scales, seven
          different LH patterns — is a complete two-week practice routine.
        </Tip>
      </Insight>

      <Insight eyebrow="Why this matters" title="Drills outside music drift away from music" accent={accent.hex}>
        <p>
          A scale played in isolation can feel mechanical even when it's clean. Plugged into
          a song, the same scale has to <em>fit</em> — it needs the right tempo, the right
          dynamics, the right phrasing. That's where technique stops being abstract and
          becomes part of how you actually play.
        </p>

        <p>
          Once you've worked the routines inside one song, transfer them to another song in
          a different key. Same drill, new shape. The transfer is what builds genuinely
          flexible hands.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-35"
        label="Lesson 35 · How to Improvise over the Blues Scale - Hit the Road Jack - Patterns (Part 1)"
        why="The final lesson of the course brings improv vocabulary back to a real song — Hit the Road Jack from Lesson 11 — using the blues-scale tools from Lesson 20."
      />
    </LessonShell>
  );
}
